import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import { trackEvent } from '../../utils/analytics';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const FinalCTA: React.FC = () => {
  const trustIndicators = [
    { label: 'No credit card required', emoji: '💳' },
  ];

  return (
    <section className="relative py-16 px-6 overflow-hidden">
      {/* Background effects - more subtle */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Subtle gradient orbs */}
        <motion.div
          animate={{
            x: [0, 30, 0],
            y: [0, -20, 0],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-1/4 left-1/4 w-[400px] h-[400px] rounded-full opacity-15"
          style={{
            background: 'radial-gradient(circle, #06B6D4 0%, transparent 60%)',
            filter: 'blur(80px)',
          }}
        />
        <motion.div
          animate={{
            x: [0, -20, 0],
            y: [0, 30, 0],
          }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-1/4 right-1/4 w-[350px] h-[350px] rounded-full opacity-15"
          style={{
            background: 'radial-gradient(circle, #3B82F6 0%, transparent 60%)',
            filter: 'blur(80px)',
          }}
        />

        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `linear-gradient(rgba(6, 182, 212, 0.2) 1px, transparent 1px),
              linear-gradient(90deg, rgba(6, 182, 212, 0.2) 1px, transparent 1px)`,
            backgroundSize: '80px 80px',
          }}
        />
      </div>

      <div className="max-w-4xl mx-auto relative">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center"
        >
          {/* Headline */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-7xl font-bold mb-6 leading-tight text-gray-900"
          >
            Start learning{' '}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
              }}
            >
              forward.
            </span>
          </motion.h2>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row justify-center gap-4 mb-8"
          >
            <Link to="/signup" onClick={() => trackEvent('cta_click', { cta_text: 'get_started', cta_location: 'final_cta' })}>
              <motion.span
                whileHover={{
                  scale: 1.05,
                  boxShadow: '0 0 80px rgba(6, 182, 212, 0.5)',
                }}
                whileTap={{ scale: 0.95 }}
                className="group relative inline-flex items-center gap-3 px-10 py-5 rounded-full font-semibold text-navy-900 text-lg transition-all overflow-hidden cursor-pointer"
                style={{
                  background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                  boxShadow: '0 0 50px rgba(6, 182, 212, 0.35)',
                }}
              >
                {/* Shine effect */}
                <motion.span
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"
                />
                <RocketLaunchIcon className="w-5 h-5 relative" />
                <span className="relative">Get Started</span>
                <motion.span
                  className="relative"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <ArrowRightIcon className="w-5 h-5" />
                </motion.span>
              </motion.span>
            </Link>

            <a href="#demo" onClick={() => trackEvent('cta_click', { cta_text: 'preview_demo', cta_location: 'final_cta' })}>
              <motion.span
                whileHover={{
                  scale: 1.05,
                  backgroundColor: 'rgba(6, 182, 212, 0.1)',
                  borderColor: 'rgba(6, 182, 212, 0.5)',
                }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 px-10 py-5 rounded-full font-semibold text-lg transition-all cursor-pointer text-gray-800 border border-gray-300"
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                Preview the demo
              </motion.span>
            </a>
          </motion.div>

          {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap justify-center items-center gap-6"
          >
            {trustIndicators.map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 + i * 0.1 }}
                whileHover={{ scale: 1.05, y: -2 }}
                className="flex items-center gap-2 text-sm px-4 py-2 rounded-full border cursor-default text-gray-500 bg-gray-100/30 border-gray-200/50"
              >
                <CheckCircleIcon className="w-4 h-4 text-green-500" />
                <span>{item.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTA;
